import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import type { CaptureResult, BeforeSendFn } from 'posthog-js';
import { hasConsent } from '~/utils/analyticsConsent';

/** Error messages that are not actionable and should be dropped. */
const NOISE_PATTERNS = [
  // Cross-origin script errors without useful info
  'Script error.',
  // Browser extensions injecting unknown objects
  'The object can not be found here',
];

function isNoiseError(event: CaptureResult | null): boolean {
  if (!event) return false;
  const props = event.properties;
  // $exception_list is an array of { type, value, stacktrace } objects
  const exceptionList: Array<{ type?: string; value?: string }> =
    props?.$exception_list ?? [];
  const messages = exceptionList
    .map((e) => e.value)
    .filter((v): v is string => typeof v === 'string');
  return messages.some((m) =>
    NOISE_PATTERNS.some((pattern) => m.includes(pattern))
  );
}

/** Returns true if the error looks like a stale-asset / chunk-load failure. */
function isChunkLoadError(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Unable to preload CSS') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Load failed')
  );
}

/**
 * Manages PostHog initialization based on cookie consent.
 *
 * PostHog is NOT initialized at app startup to avoid any network requests
 * for users who haven't consented (§ 25 TDDDG, Art. 7 DSGVO).
 * It is only initialized when a valid consent cookie exists.
 *
 * When consent is revoked, a page reload is required because PostHog has
 * no clean shutdown API — once initialized, internal timers and queues
 * continue sending requests regardless of opt_out_capturing() or reset().
 */
const PostHogConsentManager = () => {
  const initializedRef = useRef(false);
  const consented = hasConsent('analytics');

  useEffect(() => {
    if (consented) {
      if (!initializedRef.current) {
        posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          defaults: '2025-05-24',
          debug: import.meta.env.MODE === 'development',
          autocapture: false,
          capture_pageview: 'history_change',
          capture_exceptions: true,
          disable_session_recording: true,
          disable_surveys: true,
          cross_subdomain_cookie: false,
          secure_cookie: true,
          before_send: ((event: CaptureResult | null) => {
            if (isNoiseError(event)) return null;
            return event;
          }) as BeforeSendFn,
        });
        posthog.register({ app_version: import.meta.env.VITE_APP_VERSION });
        initializedRef.current = true;
      }
    } else if (initializedRef.current) {
      // PostHog has no clean shutdown API — once initialized, internal
      // timers and queues continue sending requests. A page reload is
      // the only reliable way to stop all network activity.
      window.location.reload();
    }
  }, [consented]);

  return null;
};

export default PostHogConsentManager;
export { isChunkLoadError };
